import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "nativewind/jsx-runtime";
/* eslint-disable no-restricted-imports --
   styled() 베이스. 버튼들은 렌더 시 `as={SccPressable}` 로 교체되어 정상 로깅된다.
   (터치 컴포넌트는 Scc* 사용 원칙: .eslintrc.js no-restricted-imports) */
import React, { useCallback, useRef } from 'react';
import { View, Text, TouchableOpacity, TextInput, Image } from 'react-native';
import styled from 'styled-components/native';
import SccPressable from '@/components/SccPressable';
import SccRemoteImage from '@/components/SccRemoteImage';
import { color } from '@/constant/color';
import { useLogger } from '@/logging/useLogger';
import { LogParamsProvider } from '@/logging/LogParamsProvider';
import IcOutWhite from '@/assets/icon/ic_out_white.svg';
import ImageUploader from '../components/ImageUploader';
import { useEditMode } from '../context/EditModeContext';
import { useResponsive } from '../context/ResponsiveContext';
const DEFAULT_NAVER_LIST_URL = 'https://map.naver.com';
const DEFAULT_MORE_PLACES_URL = 'https://map.naver.com';
// URL 열기 (딥링크/웹링크 모두 지원, iOS Safari 호환)
const openUrl = (url) => {
    const link = document.createElement('a');
    link.href = url;
    // 웹 링크는 새 탭에서 열기
    if (url.startsWith('http://') || url.startsWith('https://')) {
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
    }
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
/** 접근레벨별 색상 설정 */
const ACCESS_LEVEL_COLORS = {
    0: { background: '#e6f5ec', text: '#06903b' },
    1: { background: '#f0f9e7', text: '#6ac40f' },
    2: { background: '#fff8e0', text: '#ffc109' },
    3: { background: '#ffefe0', text: '#ff8c00' },
    4: { background: '#ffe8e8', text: '#e52123' },
    5: { background: '#f0e8ff', text: '#8b00ff' },
};
const getAccessLevelColors = (level) => {
    var _a;
    return (_a = ACCESS_LEVEL_COLORS[level]) !== null && _a !== void 0 ? _a : ACCESS_LEVEL_COLORS[0];
};
/** 장소 카드 컴포넌트 */
function PlaceCard({ place, isDesktop, isEditMode, }) {
    const logger = useLogger();
    const loggerRef = useRef(logger);
    loggerRef.current = logger;
    const imageUrlsToRender = [...place.imageUrls, null, null, null].slice(0, 3);
    const levelColors = getAccessLevelColors(place.accessLevel);
    const handleImageClick = useCallback((imageUrl, imageIndex) => {
        if (!isEditMode) {
            loggerRef.current.logElementClick('bbucle-road-place-image', {
                placeId: place.id,
                placeName: place.name,
                imageUrl,
                imageIndex,
            });
        }
    }, [place.id, place.name, isEditMode]);
    return (_jsx(CardContainer, { isDesktop: isDesktop, children: _jsxs(CardContent, { isDesktop: isDesktop, children: [_jsxs(TopContent, { isDesktop: isDesktop, children: [_jsx(AccessLevelBadge, { backgroundColor: levelColors.background, children: _jsxs(AccessLevelText, { textColor: levelColors.text, children: ["\uC811\uADFC\uB808\uBCA8 ", place.accessLevel] }) }), _jsxs(PlaceInfo, { children: [_jsx(PlaceName, { children: place.name }), _jsx(PlaceAddress, { children: place.address }), _jsx(PlaceHours, { children: place.businessHours }), _jsx(TagsContainer, { children: place.tags.map((tag, idx) => (_jsx(Tag, { children: _jsx(TagText, { children: tag }) }, idx))) })] })] }), _jsx(ImagesRow, { isDesktop: isDesktop, children: imageUrlsToRender.map((url, idx) => (url != null
                        ? (_jsx(PlaceImageWrapper, { as: SccPressable, isDesktop: isDesktop, onPress: () => handleImageClick(url, idx), elementName: "bbucle-road-place-image", logParams: { placeId: place.id, imageIndex: idx }, disableLogging: isEditMode, children: _jsx(PlaceImage, { source: { uri: url }, resizeMode: "cover" }) }, idx))
                        : (_jsx(PlaceImageWrapper, { isDesktop: isDesktop, children: _jsx(DummyImage, {}) }, idx)))) })] }) }));
}
export default function NearbyPlacesSection({ nearbyPlacesSection, sectionId, }) {
    var _a;
    const editContext = useEditMode();
    const isEditMode = (_a = editContext === null || editContext === void 0 ? void 0 : editContext.isEditMode) !== null && _a !== void 0 ? _a : false;
    const { isDesktop } = useResponsive();
    const iconSize = isDesktop ? 24 : 18;
    const { titleLine1, titleLine2, mapImageUrl, mobileMapImageUrl, secondMapImageUrl, mobileSecondMapImageUrl, places = [], naverListUrl = DEFAULT_NAVER_LIST_URL, morePlacesUrl = DEFAULT_MORE_PLACES_URL, wheelchairUserTipHtml, } = nearbyPlacesSection;
    const handleNaverListPress = useCallback(() => {
        openUrl(naverListUrl);
    }, [naverListUrl]);
    const handleMorePlacesPress = useCallback(() => {
        openUrl(morePlacesUrl);
    }, [morePlacesUrl]);
    const updateNearbyPlacesSection = useCallback((updates) => {
        if (!editContext)
            return;
        editContext.updateData((prev) => (Object.assign(Object.assign({}, prev), { nearbyPlacesSection: prev.nearbyPlacesSection
                ? Object.assign(Object.assign({}, prev.nearbyPlacesSection), updates) : null })));
    }, [editContext]);
    const handleMapImageChange = useCallback((url) => {
        updateNearbyPlacesSection({ mapImageUrl: url });
    }, [updateNearbyPlacesSection]);
    const handleMobileMapImageChange = useCallback((url) => {
        updateNearbyPlacesSection({ mobileMapImageUrl: url });
    }, [updateNearbyPlacesSection]);
    const handleSecondMapImageChange = useCallback((url) => {
        updateNearbyPlacesSection({ secondMapImageUrl: url });
    }, [updateNearbyPlacesSection]);
    const handleMobileSecondMapImageChange = useCallback((url) => {
        updateNearbyPlacesSection({ mobileSecondMapImageUrl: url });
    }, [updateNearbyPlacesSection]);
    const handleTitleLine1Change = useCallback((text) => {
        updateNearbyPlacesSection({ titleLine1: text });
    }, [updateNearbyPlacesSection]);
    const handleTitleLine2Change = useCallback((text) => {
        updateNearbyPlacesSection({ titleLine2: text });
    }, [updateNearbyPlacesSection]);
    return (_jsx(LogParamsProvider, { params: { displaySectionName: '근처맛집' }, children: _jsx("div", { id: sectionId, children: _jsx(Container, { isDesktop: isDesktop, children: _jsxs(ContentWrapper, { isDesktop: isDesktop, children: [_jsxs(TitleSection, { children: [_jsx(SubTitle, { isDesktop: isDesktop, children: "\uACC4\uBFCC\uD074 \uD53D!" }), isEditMode ? (_jsxs(_Fragment, { children: [_jsx(TitleLine1Input, { value: titleLine1, onChangeText: handleTitleLine1Change, placeholder: "\uD0C0\uC774\uD2C0 1\uC904 (\uAC80\uC815)", placeholderTextColor: color.gray40, isDesktop: isDesktop }), _jsx(TitleLine2Input, { value: titleLine2, onChangeText: handleTitleLine2Change, placeholder: "\uD0C0\uC774\uD2C0 2\uC904 (\uD30C\uB791)", placeholderTextColor: color.gray40, isDesktop: isDesktop })] })) : (_jsxs(_Fragment, { children: [_jsx(TitleLine1, { isDesktop: isDesktop, children: titleLine1 }), _jsx(TitleLine2, { isDesktop: isDesktop, children: titleLine2 })] }))] }), (() => {
                            const activeMapImageUrl = !isDesktop && mobileMapImageUrl ? mobileMapImageUrl : mapImageUrl;
                            const activeHandler = !isDesktop && mobileMapImageUrl ? handleMobileMapImageChange : handleMapImageChange;
                            return activeMapImageUrl ? (_jsx(MapImageContainer, { isDesktop: isDesktop, children: isEditMode ? (_jsxs(_Fragment, { children: [_jsx(SccRemoteImage, { imageUrl: activeMapImageUrl, resizeMode: "contain", style: { borderRadius: 12 } }), _jsx(ImageOverlay, { children: _jsx(ImageUploader, { currentImageUrl: activeMapImageUrl, onUploadComplete: activeHandler, compact: true }) })] })) : (_jsx(SccPressable, { onPress: handleMorePlacesPress, elementName: "bbucle-road-nearby-map-image", logParams: { imageUrl: activeMapImageUrl }, disableLogging: isEditMode, children: _jsx(SccRemoteImage, { imageUrl: activeMapImageUrl, resizeMode: "contain", style: { borderRadius: 12 } }) })) })) : (isEditMode && (_jsxs(EmptyImagePlaceholder, { children: [_jsx(EmptyImageText, { children: "\uC9C0\uB3C4 \uC774\uBBF8\uC9C0\uB97C \uC5C5\uB85C\uB4DC\uD558\uC138\uC694" }), _jsx(ImageUploader, { onUploadComplete: handleMapImageChange, buttonText: "\uC774\uBBF8\uC9C0 \uC5C5\uB85C\uB4DC" })] })));
                        })(), (() => {
                            const activeSecondMapImageUrl = !isDesktop && mobileSecondMapImageUrl ? mobileSecondMapImageUrl : secondMapImageUrl;
                            const activeSecondHandler = !isDesktop && mobileSecondMapImageUrl ? handleMobileSecondMapImageChange : handleSecondMapImageChange;
                            return activeSecondMapImageUrl ? (_jsx(MapImageContainer, { isDesktop: isDesktop, children: isEditMode ? (_jsxs(_Fragment, { children: [_jsx(SccRemoteImage, { imageUrl: activeSecondMapImageUrl, resizeMode: "contain", style: { borderRadius: 12 } }), _jsx(ImageOverlay, { children: _jsx(ImageUploader, { currentImageUrl: activeSecondMapImageUrl, onUploadComplete: activeSecondHandler, compact: true }) })] })) : (_jsx(SccPressable, { onPress: handleMorePlacesPress, elementName: "bbucle-road-nearby-map-image-2", logParams: { imageUrl: activeSecondMapImageUrl }, disableLogging: isEditMode, children: _jsx(SccRemoteImage, { imageUrl: activeSecondMapImageUrl, resizeMode: "contain", style: { borderRadius: 12 } }) })) })) : null;
                        })(), isEditMode && isDesktop && (_jsxs(MobileImageSection, { children: [_jsx(MobileImageLabel, { children: "\uBAA8\uBC14\uC77C\uC6A9 \uC9C0\uB3C4 \uC774\uBBF8\uC9C0 (\uC120\uD0DD\uC0AC\uD56D)" }), mobileMapImageUrl ? (_jsxs(MobileImagePreview, { children: [_jsx(SccRemoteImage, { imageUrl: mobileMapImageUrl, resizeMode: "contain", style: { borderRadius: 8, maxHeight: 200 }, wrapperBackgroundColor: null }), _jsx(MobileImageActions, { children: _jsx(ImageUploader, { currentImageUrl: mobileMapImageUrl, onUploadComplete: handleMobileMapImageChange, compact: true }) })] })) : (_jsx(ImageUploader, { onUploadComplete: handleMobileMapImageChange, buttonText: "\uBAA8\uBC14\uC77C \uC774\uBBF8\uC9C0 \uC5C5\uB85C\uB4DC" }))] })), isEditMode && isDesktop && secondMapImageUrl && (_jsxs(MobileImageSection, { children: [_jsx(MobileImageLabel, { children: "\uBAA8\uBC14\uC77C\uC6A9 \uB450 \uBC88\uC9F8 \uC9C0\uB3C4 \uC774\uBBF8\uC9C0 (\uC120\uD0DD\uC0AC\uD56D)" }), mobileSecondMapImageUrl ? (_jsxs(MobileImagePreview, { children: [_jsx(SccRemoteImage, { imageUrl: mobileSecondMapImageUrl, resizeMode: "contain", style: { borderRadius: 8, maxHeight: 200 }, wrapperBackgroundColor: null }), _jsx(MobileImageActions, { children: _jsx(ImageUploader, { currentImageUrl: mobileSecondMapImageUrl, onUploadComplete: handleMobileSecondMapImageChange, compact: true }) })] })) : (_jsx(ImageUploader, { onUploadComplete: handleMobileSecondMapImageChange, buttonText: "\uBAA8\uBC14\uC77C \uC774\uBBF8\uC9C0 \uC5C5\uB85C\uB4DC" }))] })), places.length > 0 && _jsx(PlacesContainer, { isDesktop: isDesktop, children: places.map((place, index) => (_jsxs(React.Fragment, { children: [_jsx(PlaceCard, { place: place, isDesktop: isDesktop, isEditMode: isEditMode }), !isDesktop && index < places.length - 1 && _jsx(Divider, {})] }, place.id))) }), _jsxs(ButtonContainer, { isDesktop: isDesktop, children: [_jsx(NaverListButton, { as: SccPressable, isDesktop: isDesktop, onPress: handleNaverListPress, elementName: "bbucle-road-naver-list", trackView: true, logParams: { url: naverListUrl }, disableLogging: isEditMode, children: _jsx(NaverListButtonText, { isDesktop: isDesktop, children: "\uB124\uC774\uBC84 \uC9C0\uB3C4\uB85C \uBAA8\uC544\uBCF4\uAE30" }) }), _jsxs(PrimaryButton, { as: SccPressable, isDesktop: isDesktop, onPress: handleMorePlacesPress, elementName: "bbucle-road-nearby-more", trackView: true, logParams: { url: morePlacesUrl }, disableLogging: isEditMode, children: [_jsx(PrimaryButtonText, { isDesktop: isDesktop, children: "\uB354 \uB9CE\uC740 \uC7A5\uC18C \uD655\uC778\uD558\uAE30" }), _jsx(IcOutWhite, { width: iconSize, height: iconSize, viewBox: "0 0 24 24", style: { 'margin-left': '-6px' } })] })] }), wheelchairUserTipHtml && (_jsxs(WheelchairUserTipBox, { isDesktop: isDesktop, children: [_jsx(WheelchairUserTipTitle, { isDesktop: isDesktop, children: "\uC774\uBBF8 \uB2E4\uB140\uC628 \uD720\uCCB4\uC5B4 \uC0AC\uC6A9\uC790\uC758 \uD6C4\uAE30\uD83E\uDDBD" }), _jsx(WheelchairUserTipContent, { isDesktop: isDesktop, children: _jsx("div", { dangerouslySetInnerHTML: { __html: wheelchairUserTipHtml } }) })] }))] }) }) }) }));
}
const Container = styled(View) `
  padding-vertical: ${({ isDesktop }) => (isDesktop ? '140px' : '80px')};
  width: 100%;
  background-color: ${color.gray10};
`;
const ContentWrapper = styled(View) `
  align-items: center;
  gap: ${({ isDesktop }) => (isDesktop ? '60px' : '40px')};
  width: 100%;
  max-width: 1020px;
  padding: 0 16px;
  align-self: center;
`;
const TitleSection = styled(View) `
  align-items: center;
  gap: 0;
  max-width: 800px;
  width: 100%;
  padding: 0 16px;
`;
const SubTitle = styled(Text) `
  font-family: Pretendard;
  font-size: ${({ isDesktop }) => (isDesktop ? '24px' : '18px')};
  font-weight: 600;
  color: ${color.brand40};
  text-align: center;
  letter-spacing: -0.456px;
  margin-bottom: 20px;
`;
const TitleLine1 = styled(Text) `
  font-family: Pretendard;
  font-size: ${({ isDesktop }) => (isDesktop ? '36px' : '24px')};
  font-weight: 700;
  color: ${color.black};
  text-align: center;
  line-height: ${({ isDesktop }) => (isDesktop ? '48px' : '34px')};
`;
const TitleLine2 = styled(Text) `
  font-family: Pretendard;
  font-size: ${({ isDesktop }) => (isDesktop ? '36px' : '24px')};
  font-weight: 700;
  color: #0e64d3;
  text-align: center;
  line-height: ${({ isDesktop }) => (isDesktop ? '48px' : '34px')};
`;
const TitleLine1Input = styled(TextInput) `
  font-family: Pretendard;
  font-size: ${({ isDesktop }) => (isDesktop ? '40px' : '24px')};
  font-weight: 700;
  color: ${color.black};
  text-align: center;
  width: 100%;
`;
const TitleLine2Input = styled(TextInput) `
  font-family: Pretendard;
  font-size: ${({ isDesktop }) => (isDesktop ? '40px' : '24px')};
  font-weight: 700;
  color: #0e64d3;
  text-align: center;
  width: 100%;
`;
const MapImageContainer = styled(View) `
  max-width: 1020px;
  width: ${({ isDesktop }) => (isDesktop ? '100%' : 'calc(100% + 32px)')};
  margin-horizontal: ${({ isDesktop }) => (isDesktop ? '0' : '-16px')};
  position: relative;
`;
/* 장소 카드 목록 컨테이너 */
const PlacesContainer = styled(View) `
  display: flex;
  flex-direction: ${({ isDesktop }) => (isDesktop ? 'row' : 'column')};
  flex-wrap: ${({ isDesktop }) => (isDesktop ? 'wrap' : 'nowrap')};
  gap: 20px;
  justify-content: center;
  width: 100%;
`;
/* 장소 카드 */
const CardContainer = styled(View) `
  background-color: ${({ isDesktop }) => (isDesktop ? color.white : 'transparent')};
  border-radius: ${({ isDesktop }) => (isDesktop ? '14px' : '0')};
  padding: ${({ isDesktop }) => (isDesktop ? '14px' : '0')};
  overflow: hidden;
  width: ${({ isDesktop }) => (isDesktop ? 'auto' : '100%')};
  ${({ isDesktop }) => isDesktop && 'flex: 1; height: 280px;'}
`;
const CardContent = styled(View) `
  ${({ isDesktop }) => isDesktop && 'flex: 1;'}
  gap: 8px;
`;
const TopContent = styled(View) `
  ${({ isDesktop }) => isDesktop && 'flex: 1;'}
  gap: 8px;
`;
/* 접근레벨 배지 */
const AccessLevelBadge = styled(View) `
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: 6px;
  padding: 4px 6px;
  align-self: flex-start;
`;
const AccessLevelText = styled(Text) `
  font-family: Pretendard;
  font-size: 12px;
  font-weight: 500;
  color: ${({ textColor }) => textColor};
  line-height: 15.6px;
`;
/* 장소 정보 */
const PlaceInfo = styled(View) `
  gap: 4px;
`;
const PlaceName = styled(Text) `
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 600;
  color: ${color.black};
  line-height: 20.8px;
`;
const PlaceAddress = styled(Text) `
  font-family: Pretendard;
  font-size: 12px;
  font-weight: 400;
  color: #6a6a73;
  line-height: 14.4px;
`;
const PlaceHours = styled(Text) `
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 400;
  color: #3a3c45;
  line-height: 18px;
  margin-top: 4px;
`;
/* 태그 */
const TagsContainer = styled(View) `
  flex-direction: row;
  gap: 6px;
  margin-top: 4px;
`;
const Tag = styled(View) `
  background-color: #eff6ff;
  border-radius: 4px;
  padding: 4px;
`;
const TagText = styled(Text) `
  font-family: Pretendard;
  font-size: 10px;
  font-weight: 400;
  color: #6a6a73;
  line-height: 12px;
`;
/* 이미지 목록 */
const ImagesRow = styled(View) `
  flex-direction: row;
  gap: 4px;
  height: ${({ isDesktop }) => (isDesktop ? '95px' : '100px')};
  margin-top: 4px;
`;
const PlaceImageWrapper = styled(View) `
  flex: 1;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;
  background-color: #d9d9d9;
`;
const PlaceImage = styled(Image) `
  width: 100%;
  height: 100%;
`;
const DummyImage = styled(Image) `
  width: 100%;
  height: 100%;
`;
/* 구분선 (모바일용) */
const Divider = styled(View) `
  height: 1px;
  background-color: #eff0f2;
  width: 100%;
`;
const ButtonContainer = styled(View) `
  flex-direction: ${({ isDesktop }) => (isDesktop ? 'row' : 'column')};
  gap: ${({ isDesktop }) => (isDesktop ? '16px' : '8px')};
  justify-content: center;
  width: ${({ isDesktop }) => (isDesktop ? 'auto' : '100%')};
`;
const NaverListButton = styled(TouchableOpacity) `
  display: flex;
  ${({ isDesktop }) => (isDesktop ? '' : 'width: 100%')};
  padding-vertical: ${({ isDesktop }) => (isDesktop ? '20px' : '12px')};
  padding-horizontal: ${({ isDesktop }) => (isDesktop ? '40px' : 'auto')};
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 100px;
  border: 1px solid ${color.brand40};
  background-color: ${color.white};
`;
const NaverListButtonText = styled(Text) `
  text-align: center;
  font-family: ${({ isDesktop }) => (isDesktop ? 'Pretendard-Bold' : 'Pretendard-Medium')};
  font-size: ${({ isDesktop }) => (isDesktop ? '20px' : '16px')};
  color: #0e64d3;
  letter-spacing: -0.36px;
  line-height: ${({ isDesktop }) => (isDesktop ? '28px' : '24px')};
`;
const PrimaryButton = styled(TouchableOpacity) `
  display: flex;
  flex-direction: row;
  ${({ isDesktop }) => (isDesktop ? '' : 'width: 100%')};
  padding-vertical: ${({ isDesktop }) => (isDesktop ? '20px' : '12px')};
  padding-horizontal: ${({ isDesktop }) => (isDesktop ? '40px' : 'auto')};
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 100px;
  background-color: ${color.brand40};
`;
const PrimaryButtonText = styled(Text) `
  text-align: center;
  font-family: ${({ isDesktop }) => (isDesktop ? 'Pretendard-SemiBold' : 'Pretendard-Medium')};
  font-weight: 600;
  font-size: ${({ isDesktop }) => (isDesktop ? '20px' : '16px')};;
  line-height: ${({ isDesktop }) => (isDesktop ? '28px' : '24px')};;
  color: ${color.white};
`;
const ImageOverlay = styled(View) `
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
`;
const EmptyImagePlaceholder = styled(View) `
  max-width: 1020px;
  width: 100%;
  padding: 60px;
  background-color: ${color.white};
  border: 2px dashed ${color.gray25};
  border-radius: 12px;
  align-items: center;
  gap: 16px;
`;
const EmptyImageText = styled(Text) `
  font-family: Pretendard;
  font-size: 16px;
  color: ${color.gray60};
`;
/* 모바일 이미지 관리 섹션 (데스크탑 Edit Mode) */
const MobileImageSection = styled(View) `
  max-width: 1020px;
  width: 100%;
  padding: 20px;
  background-color: ${color.gray10};
  border-radius: 12px;
  gap: 12px;
`;
const MobileImageLabel = styled(Text) `
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 600;
  color: ${color.gray80};
`;
const MobileImagePreview = styled(View) `
  position: relative;
  max-width: 400px;
`;
const MobileImageActions = styled(View) `
  position: absolute;
  top: 8px;
  right: 8px;
`;
/* 휠체어 사용자 팁 박스 */
const WheelchairUserTipBox = styled(View) `
  background-color: ${color.white};
  border-radius: 4px;
  padding: 16px;
  gap: 6px;
  width: ${({ isDesktop }) => (isDesktop ? '100%' : '100%')};
`;
const WheelchairUserTipTitle = styled(Text) `
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
  color: #0e64d3;
`;
const WheelchairUserTipContent = styled(View) `
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 26px;
  color: #24262b;
`;
